import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Lock, Loader2, Mail, Phone, Trash2, Search, LogOut, RefreshCw, LayoutDashboard, CalendarRange, Inbox, ListChecks, ArrowRightCircle, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { adminLogin, adminEnquiries, adminDeleteEnquiry, adminConvertEnquiry } from "@/lib/api";
import Dashboard from "@/components/admin/Dashboard";
import Diary from "@/components/admin/Diary";
import BookingsList from "@/components/admin/BookingsList";
import BookingModal from "@/components/admin/BookingModal";
import { fmtDate } from "@/components/admin/constants";

const TOKEN_KEY = "bsb_admin_token";
const TABS = [
  { id: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { id: "diary", label: "Diary", Icon: CalendarRange },
  { id: "enquiries", label: "Enquiries", Icon: Inbox },
  { id: "bookings", label: "Bookings", Icon: ListChecks },
];

function Enquiries({ token, onConverted }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(() => adminEnquiries(token).then((d) => setRows(d.enquiries || [])).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);

  const convert = async (e) => {
    try { const b = await adminConvertEnquiry(e.id, token); toast.success(`Added to bookings for ${fmtDate(b.event_date)}`); load(); onConverted?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Convert failed."); }
  };
  const remove = async (id) => { if (!window.confirm("Delete enquiry?")) return; try { await adminDeleteEnquiry(id, token); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Deleted."); } catch { toast.error("Delete failed."); } };

  const filtered = rows.filter((e) => { const s = q.trim().toLowerCase(); if (!s) return true; return [e.name, e.email, e.event_type, e.message].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)); });

  return (
    <div className="space-y-5">
      <div className="relative">
        <Search className="w-4 h-4 text-ocean/50 absolute left-4 top-1/2 -translate-y-1/2" />
        <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search enquiries..." className="w-full h-12 pl-11 pr-4 rounded-full bg-white border border-sand focus:border-coral outline-none text-sm text-ocean-deep" />
      </div>
      {filtered.length === 0 && <div className="rounded-3xl border border-dashed border-sand py-16 text-center text-ocean/50">{rows.length === 0 ? "No enquiries yet." : "No matches."}</div>}
      {filtered.map((e) => (
        <div key={e.id} data-testid="enquiry-row" className="rounded-2xl bg-white border border-sand p-5 grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-3">
            <p className="font-serif text-lg text-ocean-deep">{e.name}</p>
            <p className="text-xs text-ocean/50 mt-1">{fmtDate(e.created_at?.slice(0, 10))}</p>
            <span className="inline-block mt-2 text-[10px] overline rounded-full bg-seafoam text-ocean px-3 py-1">{e.event_type || "—"}</span>
          </div>
          <div className="md:col-span-3 space-y-1 text-sm">
            <a href={`mailto:${e.email}`} className="flex items-center gap-2 text-ocean-deep hover:text-coral break-all"><Mail className="w-3.5 h-3.5 shrink-0" />{e.email}</a>
            {e.phone && <a href={`tel:${e.phone}`} className="flex items-center gap-2 text-ocean-deep hover:text-coral"><Phone className="w-3.5 h-3.5 shrink-0" />{e.phone}</a>}
          </div>
          <div className="md:col-span-2 text-sm text-ocean/80">
            <p><span className="text-ocean/50">Date:</span> {e.event_date || "—"}</p>
            <p><span className="text-ocean/50">Guests:</span> {e.guest_count || "—"}</p>
          </div>
          <div className="md:col-span-3 text-sm text-ocean/75 leading-relaxed">{e.message || <span className="text-ocean/40">No message</span>}</div>
          <div className="md:col-span-1 flex md:flex-col gap-2 md:items-end">
            {e.booking_id ? (
              <span data-testid="enquiry-converted" className="text-[10px] overline text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Converted</span>
            ) : (
              <button data-testid="enquiry-convert" onClick={() => convert(e)} className="text-[10px] overline text-coral border border-coral/40 rounded-full px-3 py-1.5 hover:bg-coral hover:text-white transition-colors flex items-center gap-1">
                <ArrowRightCircle className="w-3 h-3" /> Convert
              </button>
            )}
            <button data-testid="enquiry-delete" onClick={() => remove(e.id)} className="p-2 rounded-full text-ocean/40 hover:bg-red-50 hover:text-red-500 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("dashboard");
  const [modal, setModal] = useState({ open: false, initial: null });
  const [refreshKey, setRefreshKey] = useState(0);

  const doLogin = async (e) => {
    e.preventDefault(); if (!password) return; setLoading(true);
    try { const { token: tk } = await adminLogin(password); localStorage.setItem(TOKEN_KEY, tk); setToken(tk); setPassword(""); toast.success("Welcome back."); }
    catch { toast.error("Wrong password."); } finally { setLoading(false); }
  };
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); };
  const openModal = (initial) => setModal({ open: true, initial });
  const closeModal = () => setModal({ open: false, initial: null });
  const onSaved = () => setRefreshKey((k) => k + 1);

  if (!token) {
    return (
      <div className="bg-cream min-h-screen">
        <Nav solid />
        <section className="max-w-md mx-auto px-5 pt-32 md:pt-40 pb-20">
          <div className="rounded-3xl bg-white border border-sand p-8 shadow-[0_20px_60px_-30px_rgba(10,31,45,0.25)]">
            <div className="w-12 h-12 rounded-full bg-coral/10 grid place-items-center mb-5"><Lock className="w-5 h-5 text-coral" /></div>
            <p className="overline text-xs text-coral mb-2">Private</p>
            <h1 className="font-serif text-3xl text-ocean-deep leading-tight mb-1">Admin Portal</h1>
            <p className="text-sm text-ocean/60 mb-6">Enter your admin password to continue.</p>
            <form onSubmit={doLogin} className="space-y-4">
              <Input data-testid="admin-password" type="password" placeholder="Admin password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 bg-cream border-sand rounded-xl" />
              <button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full rounded-full bg-coral text-white py-4 overline text-xs hover:bg-[#c9633f] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}{loading ? "Signing in..." : "Sign in"}
              </button>
            </form>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-screen">
      <Nav solid />
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-32 md:pt-36 pb-24">
        <div className="flex flex-wrap justify-between items-end gap-4 mb-8">
          <div>
            <p className="overline text-xs text-coral mb-3">Admin</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-ocean-deep leading-[1.02] tracking-tight">Booking Diary</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={() => setRefreshKey((k) => k + 1)} className="rounded-full bg-white border border-sand px-4 py-2.5 hover:border-coral transition-colors"><RefreshCw className="w-4 h-4 text-ocean" /></button>
            <button data-testid="admin-logout" onClick={logout} className="rounded-full bg-ocean-deep text-white px-4 py-2.5 text-xs overline flex items-center gap-2 hover:bg-black"><LogOut className="w-3.5 h-3.5" />Sign out</button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-sand pb-2">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 text-xs overline rounded-full px-4 py-2 transition-colors ${tab === t.id ? "bg-ocean-deep text-white" : "text-ocean hover:bg-sand/40"}`}>
              <t.Icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "dashboard" && <Dashboard token={token} onJumpTo={setTab} key={`d-${refreshKey}`} />}
        {tab === "diary" && <Diary token={token} onEdit={openModal} refreshKey={refreshKey} />}
        {tab === "enquiries" && <Enquiries token={token} onConverted={onSaved} />}
        {tab === "bookings" && <BookingsList token={token} onEdit={openModal} refreshKey={refreshKey} />}

        <BookingModal open={modal.open} onClose={closeModal} onSaved={onSaved} token={token} initial={modal.initial} />
      </section>
      <Footer />
    </div>
  );
}
